import React, { useState } from "react";
import ImageUploader from "../ImageUploader";
import RichTextEditor from "../RichTextEditor";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";

function Card({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-md border mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 border-b"
      >
        <h3 className="font-medium text-gray-800">{title}</h3>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="p-5 space-y-4">{children}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, ...rest }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-[#a08862]"
      {...rest}
    />
  );
}

export default function SectionsEditor({ content, save }) {
  const update = (key, val) => save({ [key]: val });
  const c = content;

  return (
    <div>
      <Card title="Brand">
        <Field label="Brand Name">
          <TextInput value={c.brand?.name} onChange={(v) => update("brand", { ...c.brand, name: v })} />
        </Field>
        <Field label="Logo">
          <ImageUploader
            value={c.brand?.logo}
            onChange={(v) => update("brand", { ...c.brand, logo: v })}
            aspect="square"
          />
        </Field>
      </Card>

      <Card title="Navigation Links">
        {(c.nav || []).map((n, i) => (
          <div key={i} className="flex gap-2 items-end">
            <div className="flex-1">
              <Field label={`Label ${i + 1}`}>
                <TextInput
                  value={n.label}
                  onChange={(v) => {
                    const nav = [...c.nav];
                    nav[i] = { ...nav[i], label: v };
                    update("nav", nav);
                  }}
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label="Anchor">
                <TextInput
                  value={n.href}
                  onChange={(v) => {
                    const nav = [...c.nav];
                    nav[i] = { ...nav[i], href: v };
                    update("nav", nav);
                  }}
                />
              </Field>
            </div>
            <button
              onClick={() => update("nav", c.nav.filter((_, j) => j !== i))}
              className="text-red-500 p-2"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          onClick={() => update("nav", [...(c.nav || []), { label: "NEW", href: "#" }])}
          className="flex items-center gap-1 text-sm text-[#a08862] mt-1"
        >
          <Plus size={14} /> Add link
        </button>
      </Card>

      <Card title="Hero Section">
        <Field label="Title"><TextInput value={c.hero?.title} onChange={(v) => update("hero", { ...c.hero, title: v })} /></Field>
        <Field label="Subtitle"><TextInput value={c.hero?.subtitle} onChange={(v) => update("hero", { ...c.hero, subtitle: v })} /></Field>
        <Field label="Background Image">
          <ImageUploader value={c.hero?.bgImage} onChange={(v) => update("hero", { ...c.hero, bgImage: v })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Primary CTA Label"><TextInput value={c.hero?.primaryCta?.label} onChange={(v) => update("hero", { ...c.hero, primaryCta: { ...c.hero.primaryCta, label: v } })} /></Field>
          <Field label="Primary CTA Link"><TextInput value={c.hero?.primaryCta?.href} onChange={(v) => update("hero", { ...c.hero, primaryCta: { ...c.hero.primaryCta, href: v } })} /></Field>
          <Field label="Secondary CTA Label"><TextInput value={c.hero?.secondaryCta?.label} onChange={(v) => update("hero", { ...c.hero, secondaryCta: { ...c.hero.secondaryCta, label: v } })} /></Field>
          <Field label="Secondary CTA Link"><TextInput value={c.hero?.secondaryCta?.href} onChange={(v) => update("hero", { ...c.hero, secondaryCta: { ...c.hero.secondaryCta, href: v } })} /></Field>
        </div>
      </Card>

      <Card title="About Section">
        <Field label="Eyebrow"><TextInput value={c.about?.eyebrow} onChange={(v) => update("about", { ...c.about, eyebrow: v })} /></Field>
        <Field label="Title"><TextInput value={c.about?.title} onChange={(v) => update("about", { ...c.about, title: v })} /></Field>
        <Field label="Description (rich text)">
          <RichTextEditor value={c.about?.description} onChange={(v) => update("about", { ...c.about, description: v })} />
        </Field>
        <div className="space-y-3">
          <div className="text-xs font-medium text-gray-600">Feature Cards</div>
          {(c.about?.features || []).map((f, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end border rounded p-3 bg-gray-50">
              <div className="col-span-3">
                <Field label="Lucide Icon">
                  <TextInput
                    value={f.icon}
                    onChange={(v) => {
                      const arr = [...c.about.features];
                      arr[i] = { ...arr[i], icon: v };
                      update("about", { ...c.about, features: arr });
                    }}
                  />
                </Field>
              </div>
              <div className="col-span-3">
                <Field label="Title">
                  <TextInput
                    value={f.title}
                    onChange={(v) => {
                      const arr = [...c.about.features];
                      arr[i] = { ...arr[i], title: v };
                      update("about", { ...c.about, features: arr });
                    }}
                  />
                </Field>
              </div>
              <div className="col-span-5">
                <Field label="Text">
                  <TextInput
                    value={f.text}
                    onChange={(v) => {
                      const arr = [...c.about.features];
                      arr[i] = { ...arr[i], text: v };
                      update("about", { ...c.about, features: arr });
                    }}
                  />
                </Field>
              </div>
              <button
                onClick={() => update("about", { ...c.about, features: c.about.features.filter((_, j) => j !== i) })}
                className="text-red-500 col-span-1 p-2"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              update("about", {
                ...c.about,
                features: [...(c.about?.features || []), { icon: "Sparkles", title: "New", text: "Edit me" }],
              })
            }
            className="flex items-center gap-1 text-sm text-[#a08862]"
          >
            <Plus size={14} /> Add feature
          </button>
        </div>
      </Card>

      <Card title="Menu Section Header">
        <Field label="Eyebrow"><TextInput value={c.menu?.eyebrow} onChange={(v) => update("menu", { ...c.menu, eyebrow: v })} /></Field>
        <Field label="Title"><TextInput value={c.menu?.title} onChange={(v) => update("menu", { ...c.menu, title: v })} /></Field>
        <Field label="Categories (comma separated)">
          <TextInput
            value={(c.menu?.categories || []).join(", ")}
            onChange={(v) => update("menu", { ...c.menu, categories: v.split(",").map((s) => s.trim()).filter(Boolean) })}
          />
        </Field>
        <p className="text-xs text-gray-500">Manage individual dishes in <b>Menu Items</b> page.</p>
      </Card>

      <Card title="Video Gallery" defaultOpen={false}>
        <Field label="Eyebrow"><TextInput value={c.videos?.eyebrow} onChange={(v) => update("videos", { ...c.videos, eyebrow: v })} /></Field>
        <Field label="Title"><TextInput value={c.videos?.title} onChange={(v) => update("videos", { ...c.videos, title: v })} /></Field>
        {(c.videos?.items || []).map((v, i) => (
          <div key={i} className="border rounded p-3 bg-gray-50 space-y-3">
            <Field label="Title">
              <TextInput value={v.title} onChange={(val) => {
                const arr = [...c.videos.items]; arr[i] = { ...arr[i], title: val };
                update("videos", { ...c.videos, items: arr });
              }} />
            </Field>
            <Field label="YouTube Video ID">
              <TextInput value={v.youtubeId} onChange={(val) => {
                const arr = [...c.videos.items]; arr[i] = { ...arr[i], youtubeId: val };
                update("videos", { ...c.videos, items: arr });
              }} />
            </Field>
            <Field label="Thumbnail">
              <ImageUploader value={v.thumbnail} onChange={(val) => {
                const arr = [...c.videos.items]; arr[i] = { ...arr[i], thumbnail: val };
                update("videos", { ...c.videos, items: arr });
              }} />
            </Field>
            <button onClick={() => update("videos", { ...c.videos, items: c.videos.items.filter((_, j) => j !== i) })} className="text-xs text-red-500">Delete video</button>
          </div>
        ))}
        <button onClick={() => update("videos", { ...c.videos, items: [...(c.videos?.items || []), { id: Date.now().toString(), title: "New Video", youtubeId: "", thumbnail: "" }] })} className="flex items-center gap-1 text-sm text-[#a08862]"><Plus size={14}/> Add Video</button>
      </Card>

      <Card title="Testimonials Section Header" defaultOpen={false}>
        <Field label="Eyebrow"><TextInput value={c.testimonials?.eyebrow} onChange={(v) => update("testimonials", { ...c.testimonials, eyebrow: v })} /></Field>
        <Field label="Title"><TextInput value={c.testimonials?.title} onChange={(v) => update("testimonials", { ...c.testimonials, title: v })} /></Field>
        <Field label="Google Review Link"><TextInput value={c.testimonials?.reviewLink} onChange={(v) => update("testimonials", { ...c.testimonials, reviewLink: v })} /></Field>
        <p className="text-xs text-gray-500">Add/edit reviews in <b>Testimonials</b> page.</p>
      </Card>

      <Card title="Franchise Section" defaultOpen={false}>
        <Field label="Eyebrow"><TextInput value={c.franchise?.eyebrow} onChange={(v) => update("franchise", { ...c.franchise, eyebrow: v })} /></Field>
        <Field label="Title"><TextInput value={c.franchise?.title} onChange={(v) => update("franchise", { ...c.franchise, title: v })} /></Field>
        <Field label="Subtitle"><TextInput value={c.franchise?.subtitle} onChange={(v) => update("franchise", { ...c.franchise, subtitle: v })} /></Field>
        <Field label="Description (rich text)">
          <RichTextEditor value={c.franchise?.description} onChange={(v) => update("franchise", { ...c.franchise, description: v })} />
        </Field>
        <Field label="WhatsApp Number (with country code, no +)">
          <TextInput value={c.franchise?.whatsappNumber} onChange={(v) => update("franchise", { ...c.franchise, whatsappNumber: v })} />
        </Field>
        <Field label="Budget Options (comma separated)">
          <TextInput
            value={(c.franchise?.budgetOptions || []).join(", ")}
            onChange={(v) => update("franchise", { ...c.franchise, budgetOptions: v.split(",").map((s) => s.trim()).filter(Boolean) })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Section Background Color">
            <div className="flex gap-2">
              <input
                type="color"
                value={c.franchise?.bgColor || "#1f3a2c"}
                onChange={(e) => update("franchise", { ...c.franchise, bgColor: e.target.value })}
                className="w-12 h-10 border rounded cursor-pointer"
              />
              <TextInput value={c.franchise?.bgColor || "#1f3a2c"} onChange={(v) => update("franchise", { ...c.franchise, bgColor: v })} />
            </div>
          </Field>
          <Field label="Button Color">
            <div className="flex gap-2">
              <input
                type="color"
                value={c.franchise?.buttonColor || "#25D366"}
                onChange={(e) => update("franchise", { ...c.franchise, buttonColor: e.target.value })}
                className="w-12 h-10 border rounded cursor-pointer"
              />
              <TextInput value={c.franchise?.buttonColor || "#25D366"} onChange={(v) => update("franchise", { ...c.franchise, buttonColor: v })} />
            </div>
          </Field>
        </div>
        <div className="border rounded p-3 bg-gray-50 space-y-2">
          <div className="text-xs font-medium text-gray-600">Form Placeholders (sample text shown in inputs)</div>
          {["name","email","mobile","whatsapp","city","state","info"].map((k) => (
            <div key={k} className="flex items-center gap-2">
              <span className="w-24 text-xs text-gray-500 capitalize">{k}</span>
              <input
                value={c.franchise?.placeholders?.[k] || ""}
                onChange={(e) => update("franchise", { ...c.franchise, placeholders: { ...(c.franchise?.placeholders || {}), [k]: e.target.value } })}
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
            </div>
          ))}
        </div>
      </Card>

      <Card title="Contact / Visit Us" defaultOpen={false}>
        <Field label="Eyebrow"><TextInput value={c.contact?.eyebrow} onChange={(v) => update("contact", { ...c.contact, eyebrow: v })} /></Field>
        <Field label="Title"><TextInput value={c.contact?.title} onChange={(v) => update("contact", { ...c.contact, title: v })} /></Field>
        <Field label="Address"><TextInput value={c.contact?.address} onChange={(v) => update("contact", { ...c.contact, address: v })} /></Field>
        <Field label="Phone"><TextInput value={c.contact?.phone} onChange={(v) => update("contact", { ...c.contact, phone: v })} /></Field>
        <Field label="Email"><TextInput value={c.contact?.email} onChange={(v) => update("contact", { ...c.contact, email: v })} /></Field>
        <Field label="Google Maps URL (for GET DIRECTIONS button)"><TextInput value={c.contact?.mapsUrl} onChange={(v) => update("contact", { ...c.contact, mapsUrl: v })} /></Field>
        <Field label="WhatsApp URL"><TextInput value={c.contact?.whatsappUrl} onChange={(v) => update("contact", { ...c.contact, whatsappUrl: v })} /></Field>
        <Field label="Google Maps Embed URL (the iframe src)">
          <textarea
            value={c.contact?.mapEmbedUrl || ""}
            onChange={(e) => update("contact", { ...c.contact, mapEmbedUrl: e.target.value })}
            rows={3}
            className="w-full border rounded px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#a08862]"
            placeholder="Paste the src URL from Google Maps → Share → Embed a map"
          />
          <p className="text-xs text-gray-500 mt-1">
            Tip: In Google Maps → click your business → Share → "Embed a map" → copy ONLY the src URL inside the iframe code.
          </p>
        </Field>
        <Field label="Button Color (GET DIRECTIONS)">
          <div className="flex gap-2">
            <input type="color" value={c.contact?.buttonColor || "#1f3a2c"} onChange={(e) => update("contact", { ...c.contact, buttonColor: e.target.value })} className="w-12 h-10 border rounded cursor-pointer" />
            <TextInput value={c.contact?.buttonColor || "#1f3a2c"} onChange={(v) => update("contact", { ...c.contact, buttonColor: v })} />
          </div>
        </Field>
      </Card>

      <Card title="Footer" defaultOpen={false}>
        <Field label="Tagline"><TextInput value={c.footer?.tagline} onChange={(v) => update("footer", { ...c.footer, tagline: v })} /></Field>
        <Field label="Copyright Text"><TextInput value={c.footer?.copyright} onChange={(v) => update("footer", { ...c.footer, copyright: v })} /></Field>
        <Field label="Background Color">
          <div className="flex gap-2">
            <input type="color" value={c.footer?.bgColor || "#1f3a2c"} onChange={(e) => update("footer", { ...c.footer, bgColor: e.target.value })} className="w-12 h-10 border rounded cursor-pointer" />
            <TextInput value={c.footer?.bgColor || "#1f3a2c"} onChange={(v) => update("footer", { ...c.footer, bgColor: v })} />
          </div>
        </Field>
        <Field label="Instagram URL"><TextInput value={c.footer?.instagramUrl} onChange={(v) => update("footer", { ...c.footer, instagramUrl: v })} /></Field>
        <Field label="Facebook URL"><TextInput value={c.footer?.facebookUrl} onChange={(v) => update("footer", { ...c.footer, facebookUrl: v })} /></Field>
      </Card>
    </div>
  );
}
